import { Routes, Route, Navigate } from 'react-router-dom';
import { HomePage, ComparisonSetupPage, ComparisonResultsPage, DashboardPage, DemoScoringPage, ChipPreviewPage, ProfilePage, SettingsPage } from './pages';
import { ProcessingPage } from './pages/ProcessingPage';
import { CitationsDemoPage } from './pages/CitationsDemoPage';
import { LightModeDemoPage } from './pages/LightModeDemoPage';
import AdminPage from './pages/AdminPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import MobileProfileMenuPage from './pages/MobileProfileMenuPage';
import BillingPage from './pages/BillingPage';
import LegalPage from './pages/LegalPage';
import LegalDocumentPage from './pages/LegalDocumentPage';
import EntitlementDebugPage from './pages/EntitlementDebugPage';
import BetaAccess from './pages/BetaAccess';
import { BetaRequestPage } from './pages/BetaRequestPage';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LoginPage, SignupPage, ForgotPasswordPage } from './pages/auth';
import { ProtectedRoute } from './components/ProtectedRoute';
import { BetaGate } from './components/BetaGate';
import { EmailVerificationBanner } from './components/EmailVerificationBanner';
import { PageViewTracker } from './components/analytics/PageViewTracker';

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />

      {/* Legal pages must be publicly accessible (no beta gate) */}
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/legal/:slug" element={<LegalDocumentPage />} />

      {/* Beta access request page - public (no gate) */}
      <Route path="/beta" element={<BetaRequestPage />} />

      {/* Authentication routes - always available (no gate) */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/beta-access" element={<BetaAccess />} />

      {/* Beta-gated routes - require beta access when BETA flag ON */}
      <Route path="/compare/:comparisonId/processing" element={<BetaGate><ProcessingPage /></BetaGate>} />
      <Route path="/results/:idOrSlug?" element={<BetaGate><ComparisonResultsPage /></BetaGate>} />

      {/* Protected routes requiring email verification + beta access */}
      <Route element={<ProtectedRoute requireEmailVerification />}>
        <Route path="/compare" element={<BetaGate><ComparisonSetupPage /></BetaGate>} />
        <Route path="/processing" element={<BetaGate><ProcessingPage /></BetaGate>} />
        <Route path="/dashboard" element={<BetaGate><DashboardPage /></BetaGate>} />
        <Route path="/settings" element={<BetaGate><SettingsPage /></BetaGate>} />
        <Route path="/billing" element={<BetaGate><BillingPage /></BetaGate>} />
      </Route>

      {/* Protected routes NOT requiring email verification (accessible to unverified users) + beta access */}
      <Route element={<ProtectedRoute requireEmailVerification={false} />}>
        <Route path="/profile-menu" element={<BetaGate><MobileProfileMenuPage /></BetaGate>} />
        <Route path="/profile" element={<BetaGate><ProfilePage /></BetaGate>} />
      </Route>

      <Route element={<ProtectedRoute requireAdmin />}>
        <Route path="/admin" element={<BetaGate><AdminPage /></BetaGate>} />
        <Route path="/admin/feature-flags" element={<Navigate to="/admin?tab=feature-flags" replace />} />
        <Route path="/admin/feedback" element={<Navigate to="/admin?tab=feedback" replace />} />
      </Route>

      {/* S8: Internal demo page for scoring engine validation */}
      <Route path="/demo/scoring/:comparisonId" element={<DemoScoringPage />} />

      {/* Citations demo page for isolated testing */}
      <Route path="/demo/citations" element={<CitationsDemoPage />} />

      {/* Light mode demo page for design approval */}
      <Route path="/demo/light-mode" element={<LightModeDemoPage />} />

      {import.meta.env.DEV && (
        <Route path="/dev/chip-preview" element={<ChipPreviewPage />} />
      )}

      {/* Debug page for testing entitlements */}
      <Route path="/entitlement-debug" element={<EntitlementDebugPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <FeatureFlagProvider>
          <PageViewTracker />
          <EmailVerificationBanner />
          <AppRoutes />
        </FeatureFlagProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}